import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, CardGroup, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { format, parseISO } from 'date-fns';
import { BiDollar } from 'react-icons/bi';
import { FaCheck } from 'react-icons/fa';
import Spinner from '../../shared/spinner';
import CustomAlert from '../../shared/customAlert';
import ReprovarOuPendenciarPropostaModal from '../../shared/reprovarOuPendenciarPropostaModal';
import { useVerificaPermissao } from '../../shared/useVerificaPermissao';
import { isProdutoEmprestimo } from '../../shared/constantes/tiposProduto';
import { getLabelTipoProp } from '../../shared/constantes/tiposProposta';
import { AcaoPropostaEnum } from '../../shared/constantes/acaoPropostaEnum';
import { getLabelSituacaoEsteiraEnum } from '../../shared/constantes/situacaoEsteiraEnum';
import { PropostaDTO } from '../../shared/dto/propostaDTO';
import { PromotoraEmpregadorDTO } from '../../shared/dto/promotoraEmpregadorDTO';
import { CalculoResultadoSimulacaoDTO } from '../../shared/dto/calculoResultadoSimulacaoDTO';
import { ResultadoSimulacaoPropostaDTO } from '../../shared/dto/resultadoSimulacaoPropostaDTO';
import { ReemissaoCcbDTO } from '../../shared/dto/reemissaoCcbDTO';
import { PropostaObservacao } from '../../shared/model/propostaObservacao';
import { PropostaEsteira } from '../../shared/model/propostaEsteira';
import { Financiamento } from '../../shared/model/financiamento';
import { Orgao } from '../../shared/model/orgao';
import { ClienteEndereco } from '../../shared/model/clienteEndereco';
import { Role } from '../../shared/model/role';
import PropostaService from '../../services/propostaService';
import PropostaEsteiraService from '../../services/propostaEsteiraService';
import PropostaObservacaoService from '../../services/propostaObservacaoService';
import EsteiraService from '../../services/esteiraService';
import UsuarioService from '../../services/usuarioService';
import EmpregadorService from '../../services/empregadorService';
import PromotoraEmpregadorService from '../../services/promotoraEmpregadorService';
import FinanciamentoService from '../../services/financiamentoService';
import ClienteService from '../../services/clienteService';
import { isFictor } from '../../shared/constantes/perfilFictor';
import { CompraDividaDTO } from '../../shared/dto/compraDividaDTO';
import CompraDividaReemissaoComponent from './compraDividaReemissaoComponent';

const propostaService = new PropostaService();
const propostaEsteiraService = new PropostaEsteiraService();
const propostaObservacaoService = new PropostaObservacaoService();
const esteiraService = new EsteiraService();
const usuarioService = new UsuarioService();
const empregadorService = new EmpregadorService();
const promotoraEmpregadorService = new PromotoraEmpregadorService();
const financiamentoService = new FinanciamentoService();
const clienteService = new ClienteService();

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const moeda = (valor?: number | null) =>
  (valor ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const taxa = (valor?: number | null) =>
  (valor ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });

const numeroParaMascara = (valor?: number | null) => (valor ?? 0).toFixed(2).replace('.', ',');

const mascaraMoeda = (texto: string) => {
  const digitos = texto.replace(/\D/g, '');
  if (!digitos) return '0,00';
  const comCentavos = digitos.padStart(3, '0');
  return `${parseInt(comCentavos.slice(0, -2), 10)},${comCentavos.slice(-2)}`;
};

const paraNumero = (texto: string) => parseFloat((texto || '0').replace(',', '.')) || 0;

const extrairCodigo = (valor?: string) => (valor ? valor.split('-')[0].trim() : '');

const limparCodigoTabela = (codigo?: string) => (codigo ? codigo.replace(/\s/g, '') : '');

const usaComprasDivida = (tipoProposta?: string) => {
  const tp = extrairCodigo(tipoProposta);
  return tp === 'COMPRA_DIVIDA_SAQUE' || (isFictor && (tp === 'COMPRA_DIVIDA' || tp === 'PORTABILIDADE'));
};

const converterCompraDivida = (c: any): CompraDividaDTO => ({
  bancoEntidade: c.idBanco != null ? String(c.idBanco) : '',
  numeroContrato: c.numeroContrato ?? '',
  saldoDevedor: numeroParaMascara(c.saldoDevedor),
  prazo: c.prazo ?? 0,
  parcelasPagas: c.parcelasPagas ?? 0,
  valorParcela: numeroParaMascara(c.valorParcela),
  arquivo: c.arquivo ?? null,
  nomeArquivo: c.nomeArquivo ?? '',
  tipoPagamento: c.tipoPagamento != null ? String(c.tipoPagamento) : '',
  informacaoPagamento: c.informacaoPagamento ?? '',
  selecionado: true,
  tipoConta: c.tipoConta ?? '',
  codigoBanco: c.codigoBanco ?? '',
  agencia: c.agencia ?? '',
  dvAgencia: c.dvAgencia ?? '',
  conta: c.conta ?? '',
  dvConta: c.dvConta ?? '',
  dataVencimento: c.dataVencimento ? new Date(`${String(c.dataVencimento).substring(0, 10)}T00:00:00`) : undefined
});

const mensagemErro = (err: any, padrao = 'Ocorreu um erro. Tente novamente.') => {
  const data = err?.response?.data;
  if (typeof data === 'string' && data) return data;
  if (data?.message) return data.message;
  return padrao;
};

const dataHora = (valor?: Date | string | null) =>
  valor ? format(new Date(valor), 'dd/MM/yyyy HH:mm') : '';

const Campo = ({ label, valor, md = 2, direita = false }: {
  label: string;
  valor?: string | number | null;
  md?: number;
  direita?: boolean;
}) => (
  <div className={`col-md-${md}`}>
    <div className="form-group">
      <span style={{ textAlign: 'left' }}>{label}</span>
      <input
        type="text"
        readOnly
        value={valor ?? ''}
        className="form-control form-control-sm brds"
        style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: direita ? 'right' : 'left' }}
      />
    </div>
  </div>
);

type PrevisaoFidc = {
  trocaFidc: boolean;
  idFidcAtual?: number | null;
  nomeFidcAtual?: string | null;
  idFidcNovo?: number | null;
  nomeFidcNovo?: string | null;
  erro?: string | null;
};

function PropostaReemissaoCcbComponent() {
  useVerificaPermissao();

  const navigate = useNavigate();
  const location = useLocation();
  const { idProposta, esteira } = location.state || {};

  const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState<Role | null>(null);
  const [acoesAlcada, setAcoesAlcada] = useState({ aprova: false, reprova: false, pendencia: false, altera: false });

  const [proposta, setProposta] = useState<PropostaDTO>();
  const [tpProduto, setTpProduto] = useState('');
  const [propostaEsteira, setPropostaEsteira] = useState<PropostaEsteira[]>([]);
  const [propostaObservacao, setPropostaObservacao] = useState<PropostaObservacao[]>([]);
  const [obs, setObs] = useState('');
  const [checkObsBanco, setCheckObsBanco] = useState(false);

  // Campos editaveis
  const [empregadores, setEmpregadores] = useState<PromotoraEmpregadorDTO[]>([]);
  const [empregadorSel, setEmpregadorSel] = useState('');
  const [orgaos, setOrgaos] = useState<Orgao[]>([]);
  const [orgaoSel, setOrgaoSel] = useState('');
  const [nomeCliente, setNomeCliente] = useState('');
  const [nomeMae, setNomeMae] = useState('');
  const [endereco, setEndereco] = useState<ClienteEndereco | null>(null);

  // Tabela e simulacao
  const [tabelas, setTabelas] = useState<Financiamento[]>([]);
  const [tabelaSel, setTabelaSel] = useState<Financiamento | null>(null);
  const [valorRMC, setValorRMC] = useState('0,00');
  const [valorParcela, setValorParcela] = useState('0,00');
  const [valorSolicitado, setValorSolicitado] = useState('0,00');
  const [prazos, setPrazos] = useState<number[]>([]);
  const [prazo, setPrazo] = useState('');
  const [valoresSimulacao, setValoresSimulacao] = useState<CalculoResultadoSimulacaoDTO>();
  const [resultadoSimulacaoFinal, setResultadoSimulacaoFinal] = useState<ResultadoSimulacaoPropostaDTO>();
  const [showResultadoSimulacao, setShowResultadoSimulacao] = useState(false);
  const [valoresAlterados, setValoresAlterados] = useState(false);
  const [shouldScroll, setShouldScroll] = useState(false);

  // Acoes
  const [showConfirmar, setShowConfirmar] = useState(false);
  const [showTrocaFidc, setShowTrocaFidc] = useState(false);
  const [previsaoFidc, setPrevisaoFidc] = useState<PrevisaoFidc | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [currentAction, setCurrentAction] = useState<AcaoPropostaEnum | null>(null);
  const [tipoPendenciaRecusa, setTipoPendenciaRecusa] = useState<string>('R');

  const [listaSaldoDivida, setListaSaldoDivida] = useState<CompraDividaDTO[]>([]);

  const simulacaoRef = useRef<HTMLDivElement | null>(null);

  const isEmprestimo = isProdutoEmprestimo(tpProduto);
  const usaContratosCompra = usaComprasDivida(proposta?.tipoProposta);
  const isInterno = role?.tipoAcesso === 'INTERNO';
  const ultimaEtapa = propostaEsteira[propostaEsteira.length - 1];

  // Lock da proposta e heartbeat
  useEffect(() => {
    if (!idProposta) return;
    let ativo = true;

    const realizarLockImediato = async () => {
      try {
        await propostaService.lockProposta(idProposta);
      } catch (error: any) {
        if (ativo) {
          setAlert({ message: 'Esta proposta acabou de ser bloqueada por outro usuário ou sua sessão expirou.', type: 'error' });
          navigate('/esteira-proposta/FLUXO');
        }
      }
    };
    realizarLockImediato();

    const intervalId = setInterval(async () => {
      try {
        await propostaService.heartbeatProposta(idProposta);
      } catch (error: any) {
        clearInterval(intervalId);
        setAlert({ message: 'Sua sessão de edição expirou por inatividade ou perda de conexão. A proposta foi liberada.', type: 'warning' });
        navigate('/esteira-proposta/FLUXO');
      }
    }, 30000);

    const handleTabClose = () => {
      propostaService.unlockPropostaKeepAlive(idProposta);
    };
    window.addEventListener('beforeunload', handleTabClose);

    return () => {
      ativo = false;
      clearInterval(intervalId);
      window.removeEventListener('beforeunload', handleTabClose);
      propostaService.unlockProposta(idProposta).catch(console.error);
    };
  }, [idProposta, navigate]);

  // Carga unica
  useEffect(() => {
    if (!idProposta) {
      navigate('/esteira-proposta/FLUXO');
      return;
    }
    iniciar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idProposta]);

  useEffect(() => {
    if (shouldScroll && !loading) {
      simulacaoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setShouldScroll(false);
    }
  }, [shouldScroll, loading]);

  const executar = async (fn: () => Promise<void>) => {
    setLoading(true);
    try {
      await fn();
    } catch (err: any) {
      console.error(err);
      setAlert({ message: mensagemErro(err), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Carga inicial

  const carregarUsuario = async () => {
    try {
      const id = localStorage.getItem('id') ?? '';
      const response = await usuarioService.listarUsuarioComOrigemPorId(id);
      setRole(response.data[0].usuario.role);
    } catch (err) {
      console.error('Erro ao carregar usuário:', err);
    }
  };

  const carregarAcoesAlcada = async () => {
    try {
      const response = await propostaEsteiraService.permissoesAcoesAlcada();
      setAcoesAlcada(response.data);
    } catch (err) {
      console.error('Erro ao carregar permissões de alçada:', err);
    }
  };

  const carregarEmpregadores = async (idPromotora: string) => {
    const resp = await promotoraEmpregadorService.listarEmpregadorPorPromotora(idPromotora);
    setEmpregadores(resp.data ?? []);
  };

  const carregarOrgaos = async (idEmpregador: string): Promise<Orgao[]> => {
    const resp = await empregadorService.listarOrgaoPorEmpregador(idEmpregador);
    const lista: Orgao[] = resp.data ?? [];
    setOrgaos(lista);
    return lista;
  };

  const carregarCliente = async (idCliente?: number | string) => {
    if (!idCliente) return;
    const resp = await clienteService.listarClientePorId(Number(idCliente));
    const dados = resp.data;
    const cliente = dados?.cliente ?? dados;
    setNomeMae(cliente?.nomeMae ?? '');
    const enderecos: ClienteEndereco[] = dados?.enderecos ?? cliente?.enderecos ?? [];
    const principal = enderecos.find(e => e.tipoEndereco === 'Residencial') ?? enderecos[0];
    setEndereco(principal ? { ...principal } : null);
  };

  const carregarTabelas = async (p: PropostaDTO, idEmpregador: string, produto: string): Promise<Financiamento[]> => {
    const resp = await financiamentoService.listarFinanciamentoPorPromotoraEmpregadorProposta(
      extrairCodigo(p.promotora) as any,
      idEmpregador as any,
      p.tipoProposta as any,
      produto as any,
      p.cpfCliente as any,
      p.regime
    );
    const lista: Financiamento[] = resp.data ?? [];
    setTabelas(lista);
    return lista;
  };

  const calcularLimites = async (
    p: PropostaDTO,
    tabela: Financiamento,
    idEmpregador: string,
    rmc: string,
    contratos: CompraDividaDTO[] = listaSaldoDivida
  ): Promise<CalculoResultadoSimulacaoDTO> => {
    const dto = new CalculoResultadoSimulacaoDTO();
    dto.valoresRMCCompraDivida = usaComprasDivida(p.tipoProposta)
      ? contratos.filter(item => item.selecionado).reduce((soma, item) => soma + paraNumero(item.valorParcela), 0)
      : null;
    dto.promotora = extrairCodigo(p.promotora);
    dto.empregador = idEmpregador;
    dto.financiamento = limparCodigoTabela(tabela.codigo);
    dto.produto = tabela.idProduto ?? extrairCodigo(p.produto);
    dto.valorRMC = paraNumero(rmc);
    dto.valorLimite = 0;
    dto.limiteCompra = 0;
    dto.limiteSaque = 0;
    dto.tarifaEmissao = 0;
    dto.anuidade = 0;
    dto.dtNascimentoCliente = new Date(`${String(p.dtNascimentoCliente).substring(0, 10)}T00:00:00.000Z`);

    const resp = await propostaService.simulacaoRMCTotal(dto);
    const json: CalculoResultadoSimulacaoDTO = resp.data;
    setValoresSimulacao(json);
    setPrazos(json.prazos ?? []);
    return json;
  };

  const manterPrazoOuPrimeiro = (json: CalculoResultadoSimulacaoDTO) => {
    const lista = json.prazos ?? [];
    setPrazo(prev => (lista.includes(Number(prev)) ? prev : String(lista[0] ?? '')));
  };

  const iniciar = async () => {
    setLoading(true);
    try {
      carregarUsuario();
      carregarAcoesAlcada();

      const response = await esteiraService.listarEsteiraProcessoPorIdProposta(idProposta);
      const json = response.data;
      const dto: PropostaDTO = json.propostaDTO;
      const produto: string = json.propostaDTO.tipoProduto ?? json.propostaDTO.tpProduto ?? '';

      setProposta(dto);
      setTpProduto(produto);
      setPropostaEsteira(json.esteiraDTO.propostaEsteiraList);
      setPropostaObservacao(json.esteiraDTO.propostaObservacaoList);

      const idEmpregador = extrairCodigo(dto.empregador);
      setEmpregadorSel(idEmpregador);
      setOrgaoSel(extrairCodigo(dto.orgao));
      setNomeCliente(dto.nomeCliente ?? '');
      setValorRMC(numeroParaMascara(dto.valorRMCUtilizado));
      setValorParcela(numeroParaMascara(dto.valorParcela));
      setValorSolicitado('0,00');
      setPrazo(String(dto.prazo ?? ''));

      let dividas: CompraDividaDTO[] = [];
      if (usaComprasDivida(dto.tipoProposta)) {
        const atuais = json.esteiraDTO?.propostaCompraDividaList ?? json.propostaCompraDividaList ?? [];
        dividas = (atuais as any[]).map(converterCompraDivida);
        setListaSaldoDivida(dividas);
      }

      await carregarEmpregadores(extrairCodigo(dto.promotora));
      await carregarOrgaos(idEmpregador);
      await carregarCliente(dto.idCliente);

      if (!dto.regime) {
        setAlert({ message: 'Esta proposta não possui regime cadastrado, por isso não é possível listar as tabelas para a reemissão.', type: 'error' });
        return;
      }

      const lista = await carregarTabelas(dto, idEmpregador, produto);
      const atual = lista.find(t => limparCodigoTabela(t.codigo) === limparCodigoTabela(dto.codigoTabela));
      if (atual) {
        setTabelaSel(atual);
        const limites = await calcularLimites(dto, atual, idEmpregador, numeroParaMascara(dto.valorRMCUtilizado), dividas);
        manterPrazoOuPrimeiro(limites);
      } else {
        setAlert({ message: 'A tabela atual da proposta não está mais disponível. Selecione outra tabela.', type: 'warning' });
      }
    } catch (err: any) {
      if (err.response?.status === 409) {
        navigate('/sem-permissao', { state: { message: err.response.data } });
      } else {
        console.error('Erro ao carregar proposta:', err);
        setAlert({ message: mensagemErro(err, 'Erro ao carregar dados da proposta.'), type: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Edicao

  const aoAlterarValores = () => {
    setValoresAlterados(true);
    setShowResultadoSimulacao(false);
    setResultadoSimulacaoFinal(undefined);
  };

  const handleEmpregadorChange = (idEmpregador: string) =>
    executar(async () => {
      setEmpregadorSel(idEmpregador);
      setTabelas([]);
      setTabelaSel(null);
      setValoresSimulacao(undefined);
      aoAlterarValores();
      const lista = await carregarOrgaos(idEmpregador);
      setOrgaoSel(lista[0]?.id != null ? String(lista[0].id) : '');
      await carregarTabelas(proposta!, idEmpregador, tpProduto);
    });

  const escolherTabela = (item: Financiamento) =>
    executar(async () => {
      setTabelaSel(item);
      aoAlterarValores();
      const limites = await calcularLimites(proposta!, item, empregadorSel, valorRMC);
      setPrazo(String(limites.prazos?.[0] ?? ''));
      setValorParcela(numeroParaMascara(isEmprestimo ? limites.valorRMC : limites.limiteSaque));
      setValorSolicitado('0,00');
    });

  const recalcularRmc = () => {
    if (!tabelaSel) return;
    executar(async () => {
      const limites = await calcularLimites(proposta!, tabelaSel, empregadorSel, valorRMC);
      manterPrazoOuPrimeiro(limites);
      setValorParcela(numeroParaMascara(isEmprestimo ? limites.valorRMC : limites.limiteSaque));
      setValorSolicitado('0,00');
      aoAlterarValores();
      setShouldScroll(true);
    });
  };

  useEffect(() => {
    if (!usaContratosCompra || !tabelaSel) return;
    aoAlterarValores();
    (async () => {
      try {
        const limites = await calcularLimites(proposta!, tabelaSel, empregadorSel, valorRMC);
        manterPrazoOuPrimeiro(limites);
      } catch (err: any) {
        console.error(err);
        setAlert({ message: mensagemErro(err), type: 'error' });
      }
    })();
  }, [listaSaldoDivida]);

  const handleValorRMC = (texto: string) => {
    setValorRMC(mascaraMoeda(texto));
    aoAlterarValores();
  };

  // Regra do cartao: preencheu um, o outro fica zerado. Nunca os dois, nunca nenhum.
  const handleValorParcela = (texto: string) => {
    const mascara = mascaraMoeda(texto);
    setValorParcela(mascara);
    if (paraNumero(mascara) > 0) setValorSolicitado('0,00');
    aoAlterarValores();
  };

  const handleValorSolicitado = (texto: string) => {
    const mascara = mascaraMoeda(texto);
    setValorSolicitado(mascara);
    if (paraNumero(mascara) > 0) setValorParcela('0,00');
    aoAlterarValores();
  };

  const setCampoEndereco = (campo: keyof ClienteEndereco, valor: string) =>
    setEndereco(prev => (prev ? { ...prev, [campo]: valor } : prev));

  // Simulacao

  const simular = async () => {
    if (!proposta || !tabelaSel || !valoresSimulacao) {
      setAlert({ message: 'Selecione a tabela antes de simular.', type: 'warning' });
      return;
    }

        if (usaContratosCompra && !listaSaldoDivida.some(item => item.selecionado)) {
      setAlert({ message: 'Esta proposta não possui contratos de compra de dívida para reemitir.', type: 'warning' });
      return;
    }

    if (usaContratosCompra && listaSaldoDivida.some(item => paraNumero(item.saldoDevedor) <= 0)) {
      setAlert({ message: 'Informe o Saldo Devedor de todos os contratos.', type: 'warning' });
      return;
    }

    const parcela = paraNumero(valorParcela);
    const solicitado = paraNumero(valorSolicitado);

    if (parcela > 0 && solicitado > 0) {
      setAlert({ message: 'Informe apenas o valor da parcela ou o valor solicitado!', type: 'warning' });
      return;
    }
    if (parcela === 0 && solicitado === 0) {
      setAlert({ message: 'Informe o valor da parcela ou o valor solicitado!', type: 'warning' });
      return;
    }
    if (parcela > 0) {
      const limite = isEmprestimo ? valoresSimulacao.valorRMC : valoresSimulacao.limiteSaque;
      if (parcela > Number(limite.toFixed(2))) {
        setAlert({
          message: isEmprestimo
            ? 'Informe valor da parcela menor ou igual à margem disponível'
            : 'Informe valor da parcela menor ou igual ao limite de saque',
          type: 'warning'
        });
        return;
      }
    }
    const maximoPolitica = valoresSimulacao.valorMaximoLimiteCompra || 0;
    if (solicitado > 0 && maximoPolitica > 0 && solicitado > maximoPolitica) {
      setAlert({ message: `O Valor Solicitado não pode ser maior que o Valor Máximo da política (R$ ${moeda(maximoPolitica)}).`, type: 'warning' });
      return;
    }

    const dto = new ResultadoSimulacaoPropostaDTO();
    dto.promotora = extrairCodigo(proposta.promotora);
    dto.pontoVenda = extrairCodigo(proposta.pontoVenda);
    dto.empregador = empregadorSel;
    dto.financiamento = limparCodigoTabela(tabelaSel.codigo);
    dto.produto = tabelaSel.idProduto ?? extrairCodigo(proposta.produto);
    dto.tipoProposta = extrairCodigo(proposta.tipoProposta);
    dto.valorRMC = paraNumero(valorRMC);
    dto.valorParcela = parcela;
    dto.valorSolicitado = solicitado;
    dto.prazo = parseInt(prazo, 10);
    dto.limiteSaque = valoresSimulacao.limiteSaque;
    dto.cpfCliente = proposta.cpfCliente;
    dto.nomeCliente = nomeCliente;
    dto.matriculaCliente = proposta.matriculaCliente;
    dto.dtNascimentoCliente = proposta.dtNascimentoCliente;
    dto.orgao = orgaoSel;
    dto.valorIof = 0;
    dto.valorPrincipal = 0;
    dto.valorRefin = 0;
    dto.valorCompra = 0;
    dto.contratosCompra = listaSaldoDivida.filter(item => item.selecionado).map(item => ({
      ...item,
      dataVencimento: item.dataVencimento instanceof Date
        ? format(item.dataVencimento, 'yyyy-MM-dd')
        : item.dataVencimento
    })) as any;
    dto.valorLiquido = 0;
    dto.valorBruto = 0;
    dto.taxaClAM = 0;
    dto.taxaClAA = 0;
    dto.taxaCetAM = 0;
    dto.taxaCetAA = 0;
    dto.primeiroVencimento = new Date();
    dto.ultimoVencimento = new Date();

    setLoading(true);
    try {
      const resp = await propostaService.resultadoSimulacaoParcela(dto);
      setResultadoSimulacaoFinal(resp.data);
      setShowResultadoSimulacao(true);
      setShouldScroll(true);
    } catch (err: any) {
      console.error(err);
      setAlert({ message: mensagemErro(err, 'Erro ao simular.'), type: 'error' });
      setShowResultadoSimulacao(false);
    } finally {
      setLoading(false);
    }
  };

  // Aprovar = reemitir CCB

  const validarReemissao = (): boolean => {
    if (!proposta) return false;
    if (!empregadorSel || !orgaoSel) {
      setAlert({ message: 'Informe o empregador e o órgão.', type: 'warning' });
      return false;
    }
    if (!tabelaSel) {
      setAlert({ message: 'Selecione a tabela.', type: 'warning' });
      return false;
    }
    if (!nomeCliente.trim()) {
      setAlert({ message: 'Informe o nome do cliente.', type: 'warning' });
      return false;
    }
    if (!endereco) {
      setAlert({ message: 'O cliente não possui endereço cadastrado.', type: 'warning' });
      return false;
    }
    const mudouEmpregador = empregadorSel !== extrairCodigo(proposta.empregador);
    const mudouTabela = limparCodigoTabela(tabelaSel.codigo) !== limparCodigoTabela(proposta.codigoTabela);
    if ((mudouEmpregador || mudouTabela || valoresAlterados) && !resultadoSimulacaoFinal) {
      setAlert({ message: 'Os valores foram alterados. Clique em Simular antes de aprovar.', type: 'warning' });
      return false;
    }
    return true;
  };

  const montarDto = (idFidcConfirmado?: number) => {
    const dto = new ReemissaoCcbDTO();
    dto.idProposta = Number(idProposta);
    dto.idEmpregador = Number(empregadorSel);
    dto.idOrgao = Number(orgaoSel);
    dto.idFinanciamento = tabelaSel?.id != null ? Number(tabelaSel.id) : undefined;
    dto.idProduto = tabelaSel?.idProduto ? Number(tabelaSel.idProduto) : undefined;
    dto.nomeCliente = nomeCliente.trim();
    dto.nomeMae = nomeMae.trim();
    dto.endereco = endereco!;
    if (resultadoSimulacaoFinal && valoresSimulacao) {
      dto.valorRMCUtilizado = valoresSimulacao.valorRMC;
      dto.valorLimite = valoresSimulacao.valorLimite;
      dto.limiteCompra = valoresSimulacao.limiteCompra;
      dto.limiteSaque = valoresSimulacao.limiteSaque;
      dto.tarifaEmissao = valoresSimulacao.tarifaEmissao;
      dto.anuidade = valoresSimulacao.anuidade;
      dto.simulacaoProposta = resultadoSimulacaoFinal;
    }
    dto.idFidcConfirmado = idFidcConfirmado;
    return dto;
  };

  const aoAprovar = async () => {
    if (!validarReemissao()) return;
    if (!proposta || !tabelaSel || !endereco) return;

    setLoading(true);
    try {
      const resp = await propostaService.simularTrocaFidcReemissao(montarDto());
      const previsao: PrevisaoFidc = resp.data;

      if (previsao.erro) {
        await propostaService.pendenciarReemissaoFidc(Number(idProposta), previsao.erro);
        setAlert({
          message: `Proposta pendenciada: a seleção automática de FIDC não foi concluída. Nenhuma alteração foi gravada e o contrato não foi cancelado na bancarizadora. Motivo: ${previsao.erro}`,
          type: 'error'
        });
        await sleep(3000);
        window.history.back();
        return;
      }

      if (previsao.trocaFidc) {
        setPrevisaoFidc(previsao);
        setShowTrocaFidc(true);
        return;
      }

      setShowConfirmar(true);
    } catch (err: any) {
      console.error(err);
      setAlert({ message: mensagemErro(err, 'Erro ao verificar o FIDC da proposta.'), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const confirmarReemissao = async (idFidcConfirmado?: number) => {
    setShowConfirmar(false);
    setShowTrocaFidc(false);
    if (!proposta || !tabelaSel || !endereco) return;

    setLoading(true);
    try {
      await propostaService.reemitirCcb(montarDto(idFidcConfirmado));
      setAlert({ message: 'Reemissão de CCB realizada com sucesso!', type: 'success' });
      await sleep(2000);
      window.history.back();
    } catch (err: any) {
      console.error(err);
      setAlert({ message: mensagemErro(err, 'Erro ao reemitir a CCB.'), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const pendenciarTroca = async () => {
    setShowTrocaFidc(false);
    const motivo = `Troca de FIDC não confirmada pelo operador na reemissão de CCB: de ${previsaoFidc?.nomeFidcAtual ?? 'nenhum'} para ${previsaoFidc?.nomeFidcNovo}. Nenhuma alteração foi gravada e o contrato não foi cancelado na bancarizadora.`;

    setLoading(true);
    try {
      await propostaService.pendenciarReemissaoFidc(Number(idProposta), motivo);
      setAlert({ message: 'Proposta pendenciada. Nenhuma alteração foi gravada e o contrato não foi cancelado na bancarizadora.', type: 'success' });
      await sleep(2000);
      window.history.back();
    } catch (err: any) {
      console.error(err);
      setAlert({ message: mensagemErro(err, 'Erro ao pendenciar a proposta.'), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Pendenciar e reprovar (fluxo normal da esteira)

  const acaoProposta = async (acao: AcaoPropostaEnum, idMotivo: string | null) => {
    setLoading(true);
    try {
      await propostaEsteiraService.incluirPropostaEsteira(idProposta, acao, idMotivo);
      setAlert({ message: `Ação ${acao} realizada com sucesso!`, type: 'success' });
      await sleep(2000);
      window.history.back();
    } catch (err: any) {
      console.error(err);
      setAlert({ message: `Ação ${acao}! Erro ao realizar a ação.\n${mensagemErro(err, '')}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleModalConfirm = (motivoSelect: string) => {
    if (currentAction) acaoProposta(currentAction, motivoSelect);
    setShowModal(false);
  };

  const handleClickObs = async () => {
    if (!obs.trim()) return;
    setLoading(true);
    try {
      const propostaObs = new PropostaObservacao();
      propostaObs.obsBanco = checkObsBanco ? 1 : 0;
      propostaObs.observacao = obs;
      propostaObs.proposta = Number(idProposta);
      const response = await propostaObservacaoService.inclusaoPropostaObservacao(propostaObs);
      setPropostaObservacao(anteriores => [...anteriores, response.data]);
      setObs('');
      setCheckObsBanco(false);
    } catch (err) {
      console.error('Erro ao salvar observação:', err);
      setAlert({ message: 'Erro ao salvar comentário.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const numeroProposta = proposta?.numeroProposta || '-';

  return (
    <div className="position-relative m-md-3">
      <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between' }}>
        <h5>Esteira &gt; Proposta - {numeroProposta} &gt; Reemissão de CCB</h5>
        <Button id="App-button-cor-principal" onClick={() => window.history.back()}>Voltar</Button>
      </div>
      {alert && (<CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />)}

      {loading ? <Spinner loading={loading} /> : (
        <>
          {/* ORIGEM */}
          <div className="card" style={{ marginTop: '25px' }}>
            <div className="card-header"><strong>Origem</strong></div>
            <div className="card-body">
              <div className="row">
                <Campo label="Promotora" valor={proposta?.promotora} md={3} />
                <Campo label="Supervisor" valor={proposta?.gerente} md={3} />
                <Campo label="Operador" valor={proposta?.operador} md={3} />
                <Campo label="Ponto de Venda" valor={proposta?.pontoVenda} md={3} />
              </div>
              <div className="row">
                <div className="col-md-3">
                  <div className="form-group">
                    <span style={{ textAlign: 'left' }}>Empregador</span>
                    <select
                      className="form-select form-select-sm brds"
                      id="Campo-obrigatorio"
                      value={empregadorSel}
                      onChange={(e) => handleEmpregadorChange(e.target.value)}
                    >
                      <option value="">Selecione</option>
                      {empregadores.map((emp) => (
                        <option key={emp.id} value={emp.id ?? ''}>{emp.nome}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <span style={{ textAlign: 'left' }}>Secretaria/UPAG</span>
                    <select
                      className="form-select form-select-sm brds"
                      id="Campo-obrigatorio"
                      value={orgaoSel}
                      onChange={(e) => setOrgaoSel(e.target.value)}
                    >
                      <option value="">Selecione</option>
                      {orgaos.map((org) => (
                        <option key={org.id} value={org.id ?? ''}>{org.nome}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CLIENTE */}
          <div className="card" style={{ marginTop: '25px' }}>
            <div className="card-header"><strong>Cliente</strong></div>
            <div className="card-body">
              <div className="row">
                <Campo label="CPF Cliente" valor={proposta?.cpfCliente} md={3} />
                <div className="col-md-5">
                  <div className="form-group">
                    <span style={{ textAlign: 'left' }}>Nome</span>
                    <input
                      type="text"
                      id="Campo-obrigatorio"
                      className="form-control form-control-sm brds"
                      value={nomeCliente}
                      onChange={(e) => setNomeCliente(e.target.value)}
                    />
                  </div>
                </div>
                <Campo label="Matricula" valor={proposta?.matriculaCliente} md={2} />
                <Campo
                  label="Data de Nascimento"
                  valor={proposta?.dtNascimentoCliente ? format(parseISO(String(proposta.dtNascimentoCliente).substring(0, 10)), 'dd/MM/yyyy') : ''}
                  md={2}
                />
              </div>
              <div className="row">
                <div className="col-md-5">
                  <div className="form-group">
                    <span style={{ textAlign: 'left' }}>Nome da Mãe</span>
                    <input
                      type="text"
                      className="form-control form-control-sm brds"
                      value={nomeMae}
                      onChange={(e) => setNomeMae(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              {endereco ? (
                <div className="row">
                  <div className="col-md-2">
                    <div className="form-group">
                      <span style={{ textAlign: 'left' }}>CEP</span>
                      <input type="text" maxLength={8} className="form-control form-control-sm brds" value={endereco.cep ?? ''}
                        onChange={(e) => setCampoEndereco('cep', e.target.value.replace(/\D/g, ''))} />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <span style={{ textAlign: 'left' }}>Logradouro</span>
                      <input type="text" className="form-control form-control-sm brds" value={endereco.logradouro ?? ''}
                        onChange={(e) => setCampoEndereco('logradouro', e.target.value)} />
                    </div>
                  </div>
                  <div className="col-md-1">
                    <div className="form-group">
                      <span style={{ textAlign: 'left' }}>Número</span>
                      <input type="text" className="form-control form-control-sm brds" value={endereco.numero ?? ''}
                        onChange={(e) => setCampoEndereco('numero', e.target.value)} />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form-group">
                      <span style={{ textAlign: 'left' }}>Complemento</span>
                      <input type="text" className="form-control form-control-sm brds" value={endereco.complemento ?? ''}
                        onChange={(e) => setCampoEndereco('complemento', e.target.value)} />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <span style={{ textAlign: 'left' }}>Bairro</span>
                      <input type="text" className="form-control form-control-sm brds" value={endereco.bairro ?? ''}
                        onChange={(e) => setCampoEndereco('bairro', e.target.value)} />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <span style={{ textAlign: 'left' }}>Cidade</span>
                      <input type="text" className="form-control form-control-sm brds" value={endereco.localidade ?? ''}
                        onChange={(e) => setCampoEndereco('localidade', e.target.value)} />
                    </div>
                  </div>
                  <div className="col-md-1">
                    <div className="form-group">
                      <span style={{ textAlign: 'left' }}>UF</span>
                      <input type="text" maxLength={2} className="form-control form-control-sm brds" value={endereco.uf ?? ''}
                        onChange={(e) => setCampoEndereco('uf', e.target.value.toUpperCase())} />
                    </div>
                  </div>
                </div>
              ) : (
                <span className="text-danger">O cliente não possui endereço cadastrado.</span>
              )}
            </div>
          </div>

          {/* PROPOSTA ATUAL (somente leitura) */}
          <div className="card" style={{ marginTop: '25px' }}>
            <div className="card-header">
              <strong>$ Proposta atual - <span>{numeroProposta}</span></strong>
            </div>
            <div className="card-body">
              <div className="row">
                <Campo label="Tipo de Proposta" valor={getLabelTipoProp(proposta?.tipoProposta!)} />
                <Campo label="Valor RMC Utilizado" valor={moeda(proposta?.valorRMCUtilizado)} direita />
                <Campo label="Valor Limite Cartão" valor={moeda(proposta?.valorLimite)} direita />
                <Campo label="Limite Saque" valor={moeda(proposta?.limiteSaque)} direita />
                <Campo label="Nº Contrato Consignado" valor={proposta?.numeroContratoExterno} />
              </div>
              <div className="row">
                <Campo label="Valor Solicitado" valor={moeda(proposta?.valorSolicitado)} direita />
                <Campo label="Valor Parcela" valor={moeda(proposta?.valorParcela)} direita />
                <Campo label="Prazo" valor={proposta?.prazo} />
                <Campo label="Valor Líquido" valor={moeda(proposta?.valorLiquido)} direita />
                <Campo label="Taxa CET a.m." valor={taxa(proposta?.taxaCetAm)} direita />
                <Campo label="Tabela" valor={`${proposta?.codigoTabela ?? ''} - ${proposta?.tabela ?? ''}`} md={2} />
              </div>
            </div>
          </div>

          {usaContratosCompra && (
            <CompraDividaReemissaoComponent
              lista={listaSaldoDivida}
              onChange={setListaSaldoDivida}
            />
          )}

          {/* TABELA (acima dos Valores Aprovados) */}
          <div className="card" style={{ marginTop: '25px' }}>
            <div className="card-header"><strong>Selecione a Tabela</strong></div>
            <div className="card-body">
              {tabelas.length === 0 ? (
                <span>Nenhuma tabela disponível para o empregador selecionado.</span>
              ) : (
                <Table bordered hover size="sm">
                  <thead>
                    <tr><th></th><th>Código</th><th>Tabela</th></tr>
                  </thead>
                  <tbody>
                    {tabelas.map((item) => {
                      const selecionada = tabelaSel?.codigo === item.codigo;
                      return (
                        <tr key={item.codigo} className={selecionada ? 'table-success' : undefined}>
                          <td style={{ width: '1rem' }}>
                            <Button size="sm" variant={selecionada ? 'success' : 'primary'} onClick={() => escolherTabela(item)}>
                              <FaCheck />
                            </Button>
                          </td>
                          <td>{item.codigo}</td>
                          <td>{item.nome}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </div>
          </div>

          {/* VALORES APROVADOS */}
          {tabelaSel && valoresSimulacao && (
            <div className="card" style={{ marginTop: '25px' }}>
              <div ref={simulacaoRef} className="card-header">
                <strong>Valores Aprovados - {tabelaSel.codigo} - {tabelaSel.nome}</strong>
              </div>
              <div className="card-body">
                <Row className="align-items-end mb-3">
                  <Form.Group as={Col} md="3">
                    <Form.Label>Valor RMC</Form.Label>
                    <input
                      type="text"
                      className="form-control form-control-sm brds"
                      style={{ backgroundColor: '#fff9c4' }}
                      value={valorRMC}
                      onChange={(e) => handleValorRMC(e.target.value)}
                    />
                  </Form.Group>
                  <Col md="3">
                    <Button variant="success" size="sm" onClick={recalcularRmc}>
                      <i className="fa fa-calculator" /> Recalcular RMC
                    </Button>
                  </Col>
                </Row>

                <CardGroup>
                  {!isEmprestimo ? (
                    <Card style={{ borderRadius: '0px 0px 0px 15px' }}>
                      <Card.Body>
                        <Card.Title>Proposta de Limite</Card.Title>
                        <div className="card-field">Valor RMC</div>
                        <div className="card-text">R$ {moeda(valoresSimulacao.valorRMC)}</div>
                        <div className="card-field">Limite Total</div>
                        <div className="card-text">R$ {moeda(valoresSimulacao.valorLimite)}</div>
                        <div className="card-field">Limite de Compras</div>
                        <div className="card-text">R$ {moeda(valoresSimulacao.limiteCompra)}</div>
                        <div className="card-field">Limite Saque</div>
                        <div className="card-text">
                          R$ {moeda(proposta?.tipoProposta === 'CARTAO_SEM_SAQUE' ? 0 : valoresSimulacao.limiteSaque)}
                        </div>
                      </Card.Body>
                    </Card>
                  ) : (
                    <Card>
                      <Card.Body>
                        <Card.Title>Condições do Empréstimo</Card.Title>
                        <div className="card-field">Valor Mínimo Contratado</div>
                        <div className="card-text">R$ {moeda(valoresSimulacao.valorMinContratado)}</div>
                        <div className="card-field">Valor Máximo Contratado</div>
                        <div className="card-text">R$ {moeda(valoresSimulacao.valorMaximoContratado)}</div>
                        <div className="card-field">Valor Mínimo da Parcela</div>
                        <div className="card-text">R$ {moeda(valoresSimulacao.valorMinParcela)}</div>
                        <div className="card-field">Valor Máximo da Parcela</div>
                        <div className="card-text">R$ {moeda(valoresSimulacao.valorMaxParcela)}</div>
                      </Card.Body>
                    </Card>
                  )}
                  {!isEmprestimo && (
                    <Card style={{ borderRadius: '0px 0px 15px 0px' }}>
                      <Card.Body>
                        <Card.Title>Fatura</Card.Title>
                        <div className="card-field">Dia de Corte</div>
                        <div className="card-text">{tabelaSel.diaCorte}</div>
                        <div className="card-field">Dia de Vencimento</div>
                        <div className="card-text">{tabelaSel.diaVencimento}</div>
                      </Card.Body>
                    </Card>
                  )}
                </CardGroup>

                <Row className="mb-3 mt-3 align-items-end">
                  <Form.Group as={Col} md="2">
                    <Form.Label>Valor Parcela</Form.Label>
                    <input type="text" className="form-control" value={valorParcela}
                      onChange={(e) => handleValorParcela(e.target.value)} />
                  </Form.Group>
                  <Form.Group as={Col} md="2">
                    <Form.Label>Valor Solicitado</Form.Label>
                    <input type="text" className="form-control" value={valorSolicitado}
                      onChange={(e) => handleValorSolicitado(e.target.value)} />
                  </Form.Group>
                  <Form.Group as={Col} md="3">
                    <Form.Label>Prazo</Form.Label>
                    <select className="form-select" value={prazo}
                      onChange={(e) => { setPrazo(e.target.value); aoAlterarValores(); }}>
                      {prazos.map((item) => (
                        <option key={item} value={item}>{item}</option>
                      ))}
                    </select>
                  </Form.Group>
                  <Col md="3">
                    <Button variant="primary" onClick={simular}>
                      <BiDollar /> Simular
                    </Button>
                  </Col>
                </Row>

                {showResultadoSimulacao && resultadoSimulacaoFinal && (
                  <>
                    <div className="header-cards"><b>Resultado da Simulação</b></div>
                    <CardGroup>
                      <Card style={{ borderRadius: '0px 0px 0px 15px' }}>
                        <Card.Body>
                          <Card.Title>Valores</Card.Title>
                          <div className="card-field">Valor Solicitado</div>
                          <div className="card-text">R$ {moeda(resultadoSimulacaoFinal.valorSolicitado)}</div>
                          <div className="card-field">Valor IOF</div>
                          <div className="card-text">R$ {moeda(resultadoSimulacaoFinal.valorIof)}</div>
                          <div className="card-field">Valor Líquido</div>
                          <div className="card-text">R$ {moeda(resultadoSimulacaoFinal.valorLiquido)}</div>
                          <div className="card-field">Valor Parcela</div>
                          <div className="card-text">R$ {moeda(resultadoSimulacaoFinal.valorParcela)}</div>
                        </Card.Body>
                      </Card>
                      <Card>
                        <Card.Body>
                          <Card.Title>Totais</Card.Title>
                          <div className="card-field">Valor Principal</div>
                          <div className="card-text">R$ {moeda(resultadoSimulacaoFinal.valorPrincipal)}</div>
                          <div className="card-field">Valor Bruto</div>
                          <div className="card-text">R$ {moeda(resultadoSimulacaoFinal.valorBruto)}</div>
                        </Card.Body>
                      </Card>
                      <Card>
                        <Card.Body>
                          <Card.Title>Taxas</Card.Title>
                          <div className="card-field">Taxa CL a.m.</div>
                          <div className="card-text">{taxa(resultadoSimulacaoFinal.taxaClAM)}</div>
                          <div className="card-field">Taxa CL a.a.</div>
                          <div className="card-text">{taxa(resultadoSimulacaoFinal.taxaClAA)}</div>
                          <div className="card-field">Taxa CET a.m.</div>
                          <div className="card-text">{taxa(resultadoSimulacaoFinal.taxaCetAM)}</div>
                          <div className="card-field">Taxa CET a.a.</div>
                          <div className="card-text">{taxa(resultadoSimulacaoFinal.taxaCetAA)}</div>
                        </Card.Body>
                      </Card>
                      <Card style={{ borderRadius: '0px 0px 15px 0px' }}>
                        <Card.Body>
                          <Card.Title>Prazos</Card.Title>
                          <div className="card-field">Prazo</div>
                          <div className="card-text">{resultadoSimulacaoFinal.prazo}</div>
                          <div className="card-field">Primeiro Vencimento</div>
                          <div className="card-text">{format(parseISO(String(resultadoSimulacaoFinal.primeiroVencimento)), 'dd/MM/yyyy')}</div>
                          <div className="card-field">Último Vencimento</div>
                          <div className="card-text">{format(parseISO(String(resultadoSimulacaoFinal.ultimoVencimento)), 'dd/MM/yyyy')}</div>
                        </Card.Body>
                      </Card>
                    </CardGroup>
                  </>
                )}
              </div>
            </div>
          )}

          {/* OBSERVACOES */}
          <div className="card" style={{ marginTop: '25px' }}>
            <div className="card-header"><strong>Observações</strong></div>
            <div className="card-body">
              <div
                className="form-control form-control-sm brds"
                style={{ color: 'Black', backgroundColor: 'LightGrey', height: '120px', textAlign: 'left', overflowY: 'auto', padding: '5px' }}
              >
                {propostaObservacao
                  .filter(item => (isInterno ? true : !item.obsBanco))
                  .map((item, index) => (
                    <div key={index}>
                      <span>{dataHora(item.dtInclusao)}</span>
                      <span> - {item.usuario} - </span>
                      <span>{item.observacao}</span>
                      {item.obsBanco ? (
                        <span style={{ color: 'red', fontSize: '10px', fontWeight: 'bold', marginLeft: '5px' }}>[INTERNO]</span>
                      ) : null}
                    </div>
                  ))}
              </div>
              {!proposta?.isFinalizada && (
                <div style={{ marginTop: '15px' }}>
                  <span style={{ textAlign: 'left' }}>Nova Observação</span>
                  <textarea
                    className="form-control"
                    style={{ height: '80px', textAlign: 'left', width: '100%' }}
                    value={obs}
                    onChange={(e) => setObs(e.target.value)}
                  />
                  {isInterno && (
                    <div style={{ marginTop: '5px' }}>
                      <input type="checkbox" checked={checkObsBanco} onChange={() => setCheckObsBanco(!checkObsBanco)} />
                      <label style={{ marginLeft: '5px' }}>Observação interna do Banco</label>
                    </div>
                  )}
                  <Button variant="success" style={{ marginTop: '10px' }} onClick={handleClickObs}>
                    <i className="fa fa-save" /> Gravar Observação
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* STATUS DA PROPOSTA */}
          <div className="card" style={{ marginTop: '25px' }}>
            <div className="card-header"><strong>Status da Proposta</strong></div>
            <div className="card-body">
              <table cellSpacing="2" cellPadding="4" rules="all" style={{ color: 'Black', borderColor: '#999999', borderWidth: '3px', borderStyle: 'Solid', width: '100%' }}>
                <tbody>
                  <tr className="GridHeaderStyle" id="App-button-cor-principal" style={{ color: 'White' }}>
                    <th scope="col" id="App-header-table-esquerda">Status</th>
                    <th scope="col" id="App-header-table-esquerda">Data</th>
                    <th scope="col" id="App-header-table-esquerda">Usuário</th>
                    <th scope="col" id="App-header-table-esquerda">Nr</th>
                    <th scope="col" id="App-header-table-esquerda">Atividade</th>
                    <th scope="col" id="App-header-table-esquerda">Ação</th>
                    <th scope="col" id="App-header-table-esquerda">Motivo</th>
                  </tr>
                  {propostaEsteira.map((item, index) => (
                    <tr className="GridRowStyle" key={index}>
                      <td>{getLabelSituacaoEsteiraEnum(item.situacaoProposta)}</td>
                      <td>{dataHora(item.dtInclusao)}</td>
                      <td>{item.usuario}</td>
                      <td>{item.numeroAtividade}</td>
                      <td>{item.atividade}</td>
                      <td>{item.acao ? item.acao : 'NaoDefinido'}</td>
                      <td>{item.motivoDescricao}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ACOES */}
          <div className="card" style={{ marginTop: '25px' }}>
            <div className="card-header"><strong>Ações</strong></div>
            <div className="card-body">
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                {esteira === 'S' && ultimaEtapa && ultimaEtapa.acao !== AcaoPropostaEnum.FINALIZADA ? (
                  <>
                    {acoesAlcada.aprova && (
                      <Button variant="success" onClick={aoAprovar}>
                        <i className="fa fa-thumbs-up" /> Aprovar
                      </Button>
                    )}
                    {acoesAlcada.pendencia && propostaEsteira.length > 2
                      && propostaEsteira[propostaEsteira.length - 2].acao !== AcaoPropostaEnum.PENDENCIAR && isInterno && (
                        <Button
                          style={{ backgroundColor: 'YELLOW', color: 'BLACK', borderColor: 'YELLOW' }}
                          onClick={() => {
                            setCurrentAction(AcaoPropostaEnum.PENDENCIAR);
                            setTipoPendenciaRecusa('P');
                            setShowModal(true);
                          }}
                        >
                          Pendenciar
                        </Button>
                      )}
                    {acoesAlcada.reprova && isInterno && (
                      <Button
                        variant="danger"
                        onClick={() => {
                          setCurrentAction(AcaoPropostaEnum.REPROVAR);
                          setTipoPendenciaRecusa('R');
                          setShowModal(true);
                        }}
                      >
                        Reprovar
                      </Button>
                    )}
                    <ReprovarOuPendenciarPropostaModal
                      showModal={showModal}
                      handleModalCancel={() => setShowModal(false)}
                      handleModalConfirm={handleModalConfirm}
                      tipoPendenciaRecusa={tipoPendenciaRecusa}
                    />
                  </>
                ) : null}
                <Button id="App-button-cor-principal" variant="secondary" onClick={() => window.history.back()}>
                  <i className="fa fa-backward" /> Voltar
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      <Modal show={showConfirmar} onHide={() => setShowConfirmar(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar reemissão de CCB</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          O contrato atual será cancelado na bancarizadora e incluído novamente com os dados informados.
          Essa ação não pode ser desfeita. Deseja continuar?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmar(false)}>Cancelar</Button>
          <Button variant="success" onClick={() => confirmarReemissao()}>Confirmar</Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showTrocaFidc} onHide={() => setShowTrocaFidc(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Alteração de FIDC</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Com os dados informados, o FIDC desta proposta será alterado de{' '}
            <strong>{previsaoFidc?.nomeFidcAtual ?? 'Nenhum'}</strong> para{' '}
            <strong>{previsaoFidc?.nomeFidcNovo}</strong>.
          </p>
          <p>
            <strong>A proposta ainda não foi cancelada na bancarizadora</strong> e nenhuma alteração foi gravada.
            Ao confirmar, o contrato atual será cancelado e incluído novamente com os dados informados.
            Ao pendenciar, as alterações são descartadas e a proposta é pendenciada.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="warning" onClick={pendenciarTroca}>Pendenciar</Button>
          <Button variant="success" onClick={() => confirmarReemissao(previsaoFidc?.idFidcNovo ?? undefined)}>Confirmar</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default PropostaReemissaoCcbComponent;